import dagre from "dagre";
import { Position } from "@vue-flow/core";

// Node footprint dagre lays out around. Kept in one place so the derivation-time
// history layout, the editor's auto-layout button, and any re-layout on flow-direction
// all size nodes identically — otherwise the same graph would space differently
// depending on which caller ran the layout.
const NODE_WIDTH = 240;
const NODE_HEIGHT = 120;

export type FlowDirection = "TB" | "LR";

/**
 * Pure dagre layout — positions a node/edge graph as a DAG. No Vue, no refs, no
 * component state, so it's shared verbatim by every canvas surface (history
 * derivation, editor auto-layout, flow-direction re-layout).
 *
 * Positions come from `edges` (which node points at which), never from array
 * order — a run that fans out or merges lays out as a tree, and a service that
 * finishes mid-run keeps its position because its parents haven't changed.
 *
 * Returns new node objects (spread + position/handle sides); the input array is
 * not mutated. `edges` is returned unchanged for call-site symmetry. The return
 * node type reflects the added fields, so callers passing (positionless) source
 * nodes get back nodes that satisfy VueFlow's `Node` (which requires `position`).
 */
type LaidOut<TNode> = TNode & {
  position: { x: number; y: number };
  targetPosition: Position;
  sourcePosition: Position;
};

export function getLayoutedElements<TNode extends { id: string }, TEdge extends { source: string; target: string }>(
  nodes: TNode[],
  edges: TEdge[],
  direction: FlowDirection = "TB"
): { nodes: LaidOut<TNode>[]; edges: TEdge[] } {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === "LR";
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 50, // space between nodes in the same rank
    ranksep: 100, // space between ranks
    marginx: 20,
    marginy: 20,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes: LaidOut<TNode>[] = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}
